/* Copyright (c) 2023 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import { RootState, useTivoDispatch, useTivoSelector } from '../../core/tivo/tivo-redux-wrapper';
import { CursorManager } from '../../core/navigation';
import { setSignInButtonHighlightAction } from '../../store/signin/signin.actions';
import { DeviceModel } from '../../core/device/DeviceModel';
import { Locale } from '../../core/locale/Locale';
import { LocaleKeys } from '../../core/locale/LocaleKeys';
import { onLpCodeExpiration, showSignInErrorOverlay } from '../../store/signin/signin.reducers';
import { Button } from './Button';
import { CommandmentMessage } from './CommandmentMessage';
// TODO: need to move Spinner component to a common place
import { Spinner } from '../VideoPlayerUI/Spinner';
import { LpExpiredError } from '../../core/error/LpExpiredError';
import { Log } from '../../core/logging/Log';
import { DefaultServicesClientImpl } from '../../core/impls/DefaultServiceClientImpl';
import { BrandingUiInfoKeys } from '../../core/entities/BrandingUiInfoKeys';
import { OverlayTypes } from '../../store/overlay/OverlayTypes';
import { NoNetworkOverlay } from '../NoNetworkOverlay';
import { VuLogger } from '../../core/logging';
import { hideOverlayDialog } from '../../store/overlaydialog/overlaydialog.reducers';

import './SignInScreen.scss';

const SignInScreen: FunctionComponent = (): JSX.Element => {
    const TAG = 'SignInScreen';
    const dispatch = useTivoDispatch();
    const { error, hasError, licensePlateCode, licensePlateUrl } = useTivoSelector(
        (state: RootState) => state.signin,
    );
    const hasPageRendered = useRef(false);

    const isSpinnerActive = useTivoSelector((state: RootState) => state.spinner.isSpinnerActive);

    const { isOverlayDialogActive, uiProperties } = useTivoSelector(
        (state: RootState) => state.overlayDialog,
    );

    const { isButtonHighlight } = useTivoSelector((state: RootState) => state.signin);

    const [isCodeExpired, setIsCodeExpired] = useState(false);

    const displayName =
        new DefaultServicesClientImpl().getBrandingUiInfoForKey(BrandingUiInfoKeys.DISPLAY_NAME) ||
        DeviceModel.getTiVoSettings().getDisplayName();

    const signinText = `${Locale.getString(LocaleKeys.SIGN_IN_TO)} ${displayName}`;
    const isNoNetworkOverlayActive =
        isOverlayDialogActive && uiProperties.overlayType === OverlayTypes.NO_NETWORK;
    const showErrorOverlay: boolean =
        !isNoNetworkOverlayActive && hasError && !(error instanceof LpExpiredError);

    useEffect(() => {
        VuLogger.logSignInScreenEntryEvent();
        return () => {
            VuLogger.logSignInScreenExitEvent();
        };
    }, []);

    useEffect(() => {
        if (showErrorOverlay) {
            dispatch(showSignInErrorOverlay(error));
        }
    }, [showErrorOverlay]);

    useEffect(() => {
        if (hasPageRendered.current) {
            if (hasError) {
                if (error instanceof LpExpiredError) {
                    setIsCodeExpired(true);
                    dispatch(onLpCodeExpiration());
                }
            } else {
                dispatch(hideOverlayDialog());
                setIsCodeExpired(false);
                Log.i(TAG, 'useEffect[hasError]: hasError is false.');
                Diagnostics.info(TAG, 'useEffect[hasError]: hasError is false.');
            }
        }
        hasPageRendered.current = true;
    }, [hasError]);

    const createActiveCodeContentComponents = () => {
        return (
            <div
                className="active-code-content-container"
                data-testid="active-code-content-container"
            >
                <div className="go-to-link-container" data-testid="go-to-link-container">
                    <CommandmentMessage
                        action={licensePlateUrl}
                        message={Locale.getString(LocaleKeys.SIGN_IN_GO_TO)}
                        type="link"
                    />
                </div>
                <div className="code-container" data-testid="code-container">
                    <CommandmentMessage
                        action={licensePlateCode}
                        message={Locale.getString(LocaleKeys.SIGN_IN_AND_ENTER)}
                        type="code"
                    />
                </div>
                <div className="refresh-message-container" data-testid="refresh-message-container">
                    <CommandmentMessage
                        message={Locale.getString(LocaleKeys.SIGN_IN_REFRESH_MESSAGE)}
                    />
                </div>
            </div>
        );
    };

    const [isCursorEnabled, setIsCursorEnabled] = useState(false);

    const onCursorStateChangeCallback = (state) => {
        dispatch(setSignInButtonHighlightAction(!state));
        setIsCursorEnabled(state);
    };

    useEffect(() => {
        const ref = CursorManager.subscribe(onCursorStateChangeCallback);
        return () => {
            CursorManager.unsubscribe(ref);
        };
    }, []);

    const createExpiredCodeContentComponents = () => {
        return (
            <div
                className="expired-code-content-container"
                data-testid="expired-code-content-container"
            >
                <div className="expired-message-container" data-testid="expired-message-container">
                    <CommandmentMessage
                        message={Locale.getString(LocaleKeys.SIGN_IN_CODE_EXPIRED_MESSAGE)}
                    />
                </div>
                <div
                    className="get-new-code-btn-container"
                    data-testid="get-new-code-btn-container"
                >
                    <Button
                        highlighted={
                            (!isCursorEnabled || isButtonHighlight) && !isOverlayDialogActive
                        }
                    >
                        {Locale.getString(LocaleKeys.SIGN_IN_NEW_ACTIVATION_BUTTON_LABEL)}
                    </Button>
                </div>
            </div>
        );
    };

    const createScreenContent = () => {
        const providerClasses = isCodeExpired
            ? 'provider-app-name expired-code'
            : 'provider-app-name';

        return (
            licensePlateCode && (
                <React.Fragment>
                    <div className={providerClasses} data-testid="provider-app-text">
                        {signinText}
                    </div>
                    {(isCodeExpired && createExpiredCodeContentComponents()) ||
                        createActiveCodeContentComponents()}
                </React.Fragment>
            )
        );
    };

    return (
        <React.Fragment>
            <NoNetworkOverlay />
            <div className="signin-screen-container" data-testid="signin-screen-container">
                {isSpinnerActive ? (
                    <Spinner message={Locale.getString(LocaleKeys.SIGN_IN_SPINNER_MESSAGE)} />
                ) : (
                    !showErrorOverlay && createScreenContent()
                )}
            </div>
        </React.Fragment>
    );
};

export default SignInScreen;
